import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { getString } from "common/uistringlabels/uiStringUtils";
import { CatalogicalDomain, CatalogicalService } from "lib/services";
import { FeedbackModelRecord, FeedbackModelRequest, FeedbackStats } from "lib/services/catalogical-models";
import { useMemo, useState } from "react";

export interface GetFeedbackProps {
  taxonomyId: TaxonomyIdItem;
}

export interface PostFeedbackProps {
  taxonomyId: TaxonomyIdItem;
  csrfToken: string;
  feedback: FeedbackModelRequest;
}

export interface PostFeedbackResponse {
  feedback: FeedbackModelRecord;
  itemStats: FeedbackStats;
}

export interface UseProductFeedbackProps {
  domain: CatalogicalDomain;
  defaultValues?: {
    itemFeedbackStats?: FeedbackStats;
    productFeedback?: FeedbackModelRecord[];
    userFeedback?: FeedbackModelRecord[];
  };
}

const FILTER_FOR_REVIEW = {
  content: "*",
  jobFamily: "*",
};

export function useProductFeedback(props: UseProductFeedbackProps) {
  const [itemFeedbackStats, setItemFeedbackStats] = useState<FeedbackStats | undefined>(
    props.defaultValues?.itemFeedbackStats
  );
  const [productFeedback, setProductFeedback] = useState<FeedbackModelRecord[]>(
    props.defaultValues?.productFeedback ?? []
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const client = useMemo(() => new CatalogicalService(props.domain), [props.domain]);

  async function doGetReviews(props: GetFeedbackProps) {
    setLoading(true);

    try {
      const response = await client.getProductFeedbacks(props.taxonomyId, FILTER_FOR_REVIEW);
      if (!response) {
        const err = {
          name: getString("productReviews.postErrorName"),
          message: getString("productReviews.getErrorMessage"),
        };
        setError(err);
        setProductFeedback([]);
        setLoading(false);
        return undefined;
      }
      setError(undefined);
      setProductFeedback(response.feedback);
      setLoading(false);
      return response.feedback;
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      return undefined;
    }
  }

  async function doPostFeedback(props: PostFeedbackProps) {
    setLoading(true);
    try {
      const response = await client.postFeedback(props.taxonomyId, props.feedback, props.csrfToken);
      if (!response) {
        const err = {
          name: getString("productReviews.postErrorName"),
          message: getString("productReviews.postErrorMessage"),
        };
        setError(err);
        setLoading(false);
        return undefined;
      }
      const itemInfo = await client.getCatalogItemByTaxonomyId(props.taxonomyId);
      setError(undefined);
      setProductFeedback([response, ...productFeedback]);
      setItemFeedbackStats(itemInfo?.item.feedback);
      setLoading(false);
      return {
        feedback: response,
        itemStats: itemInfo?.item.feedback as FeedbackStats,
      };
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      return undefined;
    }
  }

  return { productFeedback, itemFeedbackStats, error, isLoading, doGetReviews, doPostFeedback, setItemFeedbackStats };
}
